import OData from "odata";
import Config from "./config/config";

/*
  Intended to be the path to the API.
  Not actually used as the Cloud Service API exists at the root.
  If Cloud Service API ever requires a path then just add it here and everything should work.
*/
const apiBase = "";

const API_KEY = "apikeys";
const CREDIT_CARD = "paymentmethods";
const APPLICATION = "applicationconfigs";
const CHANNEL = "channelconfigs";
const SUBSCRIPTION = "subscriptions";
const ORGANIZATION = "organizations";
const RECORDING = "channelRecordings";
const USER = "users";
const APPLICATION_WEBHOOK = "applicationWebhooks";
const CHANNEL_WEBHOOK = "channelWebhooks";
const APP_STATS = "dashboard/applicationstats";
const DASHBOARD_QUERY = "dashboard/query";
const SDK_STABLE = "sdk/stable";
const SDK_BETA = "sdk/beta";
const BILLING = "billing/usage";
const API_VERSION = "1.0";

const tooManyRequestsMessage =
  "The service is temporarily unavailable. Please try again in a few minutes. If the issue persists, please contact support@liveswitch.io";
const requestForbiddenMessage =
  "Request not allowed. Please contact support@liveswitch.io";

const REFRESH_TOKEN_KEY = "refresh_token";
const REFRESH_TOKEN_ENDPOINT = "auth0/refreshAccessToken";
const ACCESS_TOKEN_KEY = "access_token";

export default {
  props: {
    endPoints: {
      appStats: APP_STATS,
      apiKey: API_KEY,
      application: APPLICATION,
      creditCard: CREDIT_CARD,
      channel: CHANNEL,
      subscription: SUBSCRIPTION,
      applicationWebhook: APPLICATION_WEBHOOK,
      channelWebhook: CHANNEL_WEBHOOK,
      dashboardQuery: DASHBOARD_QUERY,
      organization: ORGANIZATION,
      recording: RECORDING,
      user: USER,
      latestStableSdk: SDK_STABLE,
      latestBetaSdk: SDK_BETA,
      billing: BILLING,
    },
    versionNumber: API_VERSION,
    config: new Config()
  },
  log: function(msg, error) {
    if (!this.debug) {
      // eslint-disable-next-line
      console.log(msg, error);
    }
  },
  /* o default set up. */
  o: function(paramters) {
    if (localStorage.apiKey) {
      OData().config({
        autoFormat: false,
        headers: [
          {
            name: "X-API-Key",
            value: localStorage.apiKey
          },
          {
            name: "Content-Type",
            value: "application/json; IEEE754Compatible=false"
          }
        ]
      });
    } else if (localStorage.access_token) {
      OData().config({
        autoFormat: false,
        headers: [
          {
            name: "Authorization",
            value: "bearer " + localStorage.access_token
          },
          {
            name: "Content-Type",
            value: "application/json; IEEE754Compatible=false"
          }
        ]
      });
    } else {
      OData().config({
        headers: [
          { name: "Authorization", value: "none" },
          { name: "Content-Type", value: "application/json" }
        ]
      });
    }
    OData().config({
      endpoint: this.props.config.serviceBase
    });
    return OData(paramters);
  },
  /* Convenience helpers. */
  deletePropertiesOnEntity: function(entity, deleteProperties, deleteId) {
    var copy = entity;
    if (
      deleteProperties &&
      Array.isArray(deleteProperties) &&
      deleteProperties.length > 0
    ) {
      copy = JSON.parse(JSON.stringify(entity));
      deleteProperties.forEach(function(element) {
        if (copy.hasOwnProperty(element)) {
          delete copy[element];
        }
      });
    } else if (copy.hasOwnProperty("id") && deleteId) {
      copy = JSON.parse(JSON.stringify(entity));
    }
    // Patch and Post do not like id in the body
    if (copy.hasOwnProperty("id") && deleteId) {
      delete copy["id"];
    }
    return copy;
  },
  refreshAccessToken: function () {
    var me = this;
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return new Promise(function(resolve, reject) {
      if (refreshToken) {
        me.get(REFRESH_TOKEN_ENDPOINT, [
          { k: "refreshToken", v: refreshToken },
        ]).then((result) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, result);
          resolve();
        });
      } else {
        reject();
      }
    });
  },
  handleRequestErrors: function (ex, resolve, reject, callback, params) {
    var me = this;
    if (ex.code === 429) {
      ex.msg = tooManyRequestsMessage;
      reject(ex);
    } else if (ex.code === 403) {
      ex.msg = requestForbiddenMessage;
      reject(ex);
    } else if (ex.code === 401) {
      me.refreshAccessToken().then(() => {
        params.push(false); // will not retry on fail
        callback
          .apply(me, params)
          .then((entity) => {
            resolve(entity);
          });
      });
    } else {
      reject(ex);
    }
  },
  formatQueryParams: function(queryParams) {
    return queryParams
      ? queryParams
          .map(x => {
            return `&${x.k}=${x.v}`;
          })
          .join("")
      : "";
  },
  /* o CRUD helpers. */
  get: function (endPoint, queryParams, willRetry = true) {
    var me = this;
    var qP = this.formatQueryParams(queryParams);
    return new Promise(function(resolve, reject) {
      me.o(`${apiBase}${endPoint}?api-version=${API_VERSION}${qP}`).get(
        function(entity) {
          resolve(entity);
        },
        function(ex) {
          if (willRetry) {
            me.handleRequestErrors(ex, resolve, reject, me.get, [
              endPoint,
              queryParams,
            ]);
          } else {
            reject(ex);
          }
        }
      );
    });
  },

  getConnectionAudioRecordingUrl: function (
    applicationId,
    connectionRecordingId,
    queryParams
  ) {
    const me = this;
    const qP = this.formatQueryParams(queryParams);
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}Applications/${applicationId}/ConnectionRecordings/${connectionRecordingId}/audioUrl?api-version=${API_VERSION}${qP}`
      ).get(
        function (entity) {
          resolve(entity);
        },
        function (ex) {
          me.handleRequestErrors(ex, resolve, reject, me.get, [
            applicationId, 
            connectionRecordingId,
            queryParams,
          ]);
        }
      );
    });
  },
  getConnectionVideoRecordingUrl: function (
    applicationId,
    connectionRecordingId,
    queryParams
  ) {
    const me = this;
    const qP = this.formatQueryParams(queryParams);
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}Applications/${applicationId}/ConnectionRecordings/${connectionRecordingId}/videoUrl?api-version=${API_VERSION}${qP}`
      ).get(
        function (entity) {
          resolve(entity);
        },
        function (ex) {
          me.handleRequestErrors(ex, resolve, reject, me.get, [
            applicationId, 
            connectionRecordingId,
            queryParams,
          ]);
        }
      );
    });
  },
  getConnectionRecordingsDownload: function (
    applicationId,
    channelId,
    channelRecordingId,
    queryParams
  ) {
    const me = this;
    const qP = this.formatQueryParams(queryParams);
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}Applications/${applicationId}/ConnectionRecordings?$filter=channelId eq '${channelId}' and channelRecordingId eq ${channelRecordingId} &api-version=${API_VERSION}${qP}`
        ).get(
        function (entity) {
          let connectionRecordings = entity;
          let connectionIds = [];
          connectionRecordings.forEach(connectionRecording =>{
            connectionIds.push([connectionRecording.applicationId, connectionRecording.id]);
          });
          let urls = [];
          connectionIds.forEach(id =>{ 
            urls.push(me.getConnectionVideoRecordingUrl(id[0],id[1],null));
            urls.push(me.getConnectionAudioRecordingUrl(id[0],id[1],null));
          })
          Promise.all(urls).then(result =>
          {
            //returns the array of video and audio urls to be downloaded.
            resolve(result);
          });
        },
        function (ex) {
          console.error(ex);
          me.handleRequestErrors(ex, resolve, reject, me.get, [
            applicationId, 
            channelId, 
            channelRecordingId,
            queryParams,
          ]);
        }
      );
    });
  },
  getChannelRecordings: function(applicationId, channelId, stopTimestamp, startTimestamp, ready, queryParams) {
    var me = this;
    var qP = this.formatQueryParams(queryParams);
    const filters = [];
    if (channelId != null && channelId != '') {
      filters[0] = "channelId eq '" + channelId + "'";
    }
    if (stopTimestamp != null) {
      filters[1] = "stopTimestamp lt " + stopTimestamp.toISOString();
    }
    if (startTimestamp != null) {
      filters[2] = "startTimestamp ge " + startTimestamp.toISOString();
    }
    if (ready != null) {
      filters[3] = "ready eq " + ready.toString();
    }
    var filterstring = filters.filter(Boolean).join(" and ");
    if (filterstring != "") {
      filterstring = "&$filter=" + filterstring;
    }
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}Applications/${applicationId}/ChannelRecordings?api-version=${API_VERSION}${filterstring}${qP}`
      ).get(
        function(entity) {
          resolve([{ value: entity }, { count: this.inlinecount }]);
        },
        function(ex) {
          me.handleRequestErrors(ex, resolve, reject, me.get, [
            "ChannelRecordings",
            queryParams,
          ]);
        }
      );
    });
  },
  getChannelRecordingUrl: function (
    applicationId,
    channelId,
    channelRecordingId,
    queryParams
  ) {
    var me = this;
    var qP = this.formatQueryParams(queryParams);
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}Applications/${applicationId}/Channels/${channelId}/ChannelRecordings/${channelRecordingId}/url?api-version=${API_VERSION}${qP}`
      ).get(
        function (entity) {
          resolve(entity);
        },
        function (ex) {
          me.handleRequestErrors(ex, resolve, reject, me.get, [
            applicationId, 
            channelId, 
            channelRecordingId,
            queryParams,
          ]);
        }
      );
    });
  },
  getById: function (endPoint, id, willRetry = true) {
    var me = this;
    return new Promise(function(resolve, reject) {
      me.o(`${apiBase}${endPoint}('${id}')?api-version=${API_VERSION}`).get(
        function(entity) {
          resolve(entity);
        },
        function (ex) {
          if (willRetry) {
            me.handleRequestErrors(ex, resolve, reject, me.getById, [
              endPoint,
              id,
            ]);
          } else {
            reject(ex);
          }
        }
      );
    });
  },
  getByFilter: function (endPoint, key, value, comparator, willRetry = true) {
    var me = this;
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}${endPoint}?$filter=${key} ${comparator} ${value}&api-version=${API_VERSION}`
      ).get(
        function(entity) {
          resolve(entity);
        },
        function(ex) {
          if (willRetry) {
            me.handleRequestErrors(ex, resolve, reject, me.getByFilter, [
              endPoint,
              key,
              value,
              comparator,
            ]);
          } else {
            reject(ex);
          }
        }
      );
    });
  },
  post: function (
    endPoint,
    entity,
    deleteProperties,
    endPointSuffix,
    willRetry = true
  ) {
    var me = this;
    var copy = this.deletePropertiesOnEntity(entity, deleteProperties, true);
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}${endPoint}${endPointSuffix ||
          ""}?api-version=${API_VERSION}`
      )
        .post(copy)
        .save(
          function(entity) {
            resolve(entity);
          },
          function(ex) {
            if (willRetry) {
              me.handleRequestErrors(ex, resolve, reject, me.post, [
                endPoint,
                entity,
                deleteProperties,
                endPointSuffix,
              ]);
            } else {
              reject(ex);
            }
          }
        );
    });
  },
  put: function (
    endPoint,
    entity,
    deleteProperties,
    endPointSuffix,
    willRetry = true
  ) {
    var me = this;
    var copy = this.deletePropertiesOnEntity(entity, deleteProperties, false);
    return new Promise(function(resolve, reject) {
      me.o(
        `${apiBase}${endPoint}(${entity.id})${endPointSuffix ||
          ""}?api-version=${API_VERSION}`
      )
        .put(copy)
        .save(
          function() {
            resolve();
          },
          function(ex) {
            if (willRetry) {
              me.handleRequestErrors(ex, resolve, reject, me.put, [
                endPoint,
                entity,
                deleteProperties,
                endPointSuffix,
              ]);
            } else {
              reject(ex);
            }
          }
        );
    });
  },
  patch: function (
    endPoint,
    entity,
    deleteProperties,
    endPointSuffix,
    willRetry = true
  ) {
    var me = this;
    var copy = this.deletePropertiesOnEntity(entity, deleteProperties, true);
    var path = `${apiBase}${endPoint}`;
    if (entity.id) {
      path = `${apiBase}${endPoint}('${entity.id}')${endPointSuffix ||
        ""}?api-version=${API_VERSION}`;
    }
    return new Promise(function(resolve, reject) {
      me.o(path)
        .patch(copy)
        .save(
          function() {
            resolve();
          },
          function(ex) {
            if (willRetry) {
              me.handleRequestErrors(ex, resolve, reject, me.patch, [
                endPoint,
                entity,
                deleteProperties,
                endPointSuffix,
              ]);
            } else {
              reject(ex);
            }
          }
        );
    });
  },
  delete: function (endPoint, id, willRetry = true) {
    var me = this;
    return new Promise(function(resolve, reject) {
      me.o(`${apiBase}${endPoint}('${id}')?api-version=${API_VERSION}`)
        .delete()
        .save(
          function() {
            resolve();
          },
          function(ex) {
            if (willRetry) {
              me.handleRequestErrors(ex, resolve, reject, me.delete, [
                endPoint,
                id,
              ]);
            } else {
              reject(ex);
            }
          }
        );
    });
  },
  deleteByPath: function(endPoint, id) {
    var me = this;
    return new Promise(function(resolve, reject) {
      me.o(`${apiBase}${endPoint}/${id}?api-version=${API_VERSION}`)
        .delete()
        .save(
          function() {
            resolve();
          },
          function(ex) {
            me.handleRequestErrors(ex, resolve, reject, me.delete, [
              endPoint,
              id,
            ]);
          }
        );
    });
  },
  deleteRecording: function (applicationId, channelId, id) {
    var me = this;
    //copying the format used in odata's o.js to pass in the request as an object
    var reqObj = {
      path: [
        {
          resource: `Applications/${applicationId}/Channels/${channelId}/ChannelRecordings`,
          get: id,
        }
      ],
      appending: "",
      query: {},
      queryList: [{ name: "api-version", value: API_VERSION }],
      method: "DELETE",
      data: null,
      progress: null,
    };

    return new Promise(function(resolve, reject) {
      me.o(`${apiBase}`)
        .delete(reqObj)
        .save(
          function() {
            resolve();
          },
          function(ex) {
            me.handleRequestErrors(ex, resolve, reject, me.delete, [
              applicationId,
              channelId,
              id,
            ]);
          }
        );
    });
  },
  /* Generic CRUD helpers. */
  getEntity: function(endPoint, id, queryParams) {
    var me = this;
    var onFailure = function(ex) {
      me.log("Failed to get entity.", ex);
    };

    return new Promise(function(resolve, reject) {
      if (!id) {
        me.get(endPoint, queryParams)
          .then(function(entity) {
            resolve(entity);
          })
          .catch(function(ex) {
            onFailure(ex);
            reject(ex);
          });
      } else {
        me.getById(endPoint, id)
          .then(function(entity) {
            resolve(entity);
          })
          .catch(function(ex) {
            onFailure(ex);
            reject(ex);
          });
      }
    });
  },
  setEntity: function(endPoint, entity, deleteProperties, endPointSuffix) {
    var me = this;
    var onFailure = function(ex) {
      me.log("Failed to set entity.", ex);
    };
    return new Promise(function(resolve, reject) {
      if (entity.id) {
        me.patch(endPoint, entity, deleteProperties, endPointSuffix)
          .then(function() {
            resolve();
          })
          .catch(function(ex) {
            onFailure(ex);
            reject(ex);
          });
      } else {
        me.post(endPoint, entity, deleteProperties, endPointSuffix)
          .then(function(entity) {
            resolve(entity);
          })
          .catch(function(ex) {
            onFailure(ex);
            reject(ex);
          });
      }
    });
  }
};
